/* eslint-disable no-param-reassign */
import {enumContainer, layoutConfigModel, utils} from '@wallaby-ui/core';
import {filter, forEach, forOwn, get, includes, isEmpty, isString, map, merge, some, union} from 'lodash';
import {Layout} from 'plotly.js';
import {IntlShape} from 'react-intl';
import {TABLE_TYPE_AG} from 'src/js/model/column_config';
import {TableConfig, TABLE_CONFIG} from 'src/js/model/table_config';

import {SearchTypeViews} from './search_type_views';

const REPORT_DATA_SOURCE = 'dataSource';
const REPORT_DATA_TYPE = 'dataType';

const REPORT_LABEL = 'label';
const REPORT_OPTIONS = 'options';
const REPORT_CHART_RESIZE = 'resizeHandler';
const REPORT_CHART_EMPTY_MESSAGE = 'emptyMessage';
const REPORT_TYPE = 'type';
const REPORT_ID = 'id';
export const REPORT_TYPE_CHART = 'chart';
export const REPORT_TYPE_SUMMARY = 'summary';
export const REPORT_TYPE_TABLE = 'table';
// report table with be paging
export const REPORT_TYPE_PAGING_TABLE = 'paging_table';

const REPORT_TABLE_LAYOUT = 'layout';

const REPORT_CHART_CONFIG = 'config';

const REPORT_CHART_DATA = 'data';
const REPORT_CHART_DATA_BINDINGS = 'dataBindings';
const REPORT_CHART_LAYOUT = 'layout';
const REPORT_CHART_STYLE = 'style';
const REPORT_CHART_CLASS = 'className';
const REPORT_CHART_AXIS_TICKFORMAT = 'tickformat';
const REPORT_CHART_AXIS_TICKFORMATSTOPS = 'tickformatstops';
const REPORT_CHART_AXIS_TITLE = 'title';
const REPORT_CHART_LAYOUT_TEXT_DIMENSION_HOVER_LABEL = 'dlTextDimensionHoverLabel';
const REPORT_CHART_LAYOUT_TICK_LABEL_MAX_LENGTH = 'dlTickLabelMaxLength';
const REPORT_CHART_LAYOUT_SKIP_AXIS_TICK_LABEL_TRIMMING = 'dlSkipAxisTickLabelTrimming';
const REPORT_CHART_LAYOUT_SKIP_HOVER_TEMPLATE = 'dlSkipHoverTemplate';
const REPORT_CHART_TRANSLATION_CONFIG = 'translationConfig';
const REPORT_CHART_TRANSLATION_PREFIX = 'translationPrefix';
const REPORT_CHART_TRANSLATION_ENUM_NAME = 'enumName';
const REPORT_DOWNLOADED_IMAGE_TITLE = 'downloadedImageTitle';

const REPORT_CHART_DATE_LOCALE = '_DATE_LOCALE_';
const REPORT_CHART_DATE_TIME_LOCALE = '_DATE_TIME_LOCALE_';
const REPORT_CHART_DATE_TIME_LOCALE_STOPS = '_DATE_TIME_LOCALE_STOPS_';

const REPORT_SUMMARY_DATA = 'data';
const REPORT_SUMMARY_GENERATED_LAYOUT = 'generatedLayout';

const REPORT_SUMMARY_DATA_ID = 'id';
const REPORT_SUMMARY_DATA_DATA_BINDINGS = 'dataBindings';
const REPORT_SUMMARY_DATA_TYPE = 'type';
const REPORT_SUMMARY_DATA_VALUE = 'value';

export const REPORT_SUMMARY_DATA_TYPE_TEXT = 'text';
export const REPORT_SUMMARY_DATA_TYPE_VALUE = 'value';
export const REPORT_SUMMARY_DATA_TYPE_DATE = 'dateValue';

const DATA_BINDING_DATA = 'data';
const DATA_BINDING_TYPE = 'type';
const DATA_BINDING_TRACE_PROPERTY = 'traceProperty';
const DATA_BINDING_TRACE_VALUE = 'traceValue';
export const DATA_BINDING_TYPE_SIMPLE = 'simple';
export const DATA_BINDING_TYPE_TRACE = 'trace';
export const DATA_BINDING_TYPE_TRACE_FROM_DATA = 'traceFromData';
const DATA_BINDING_STORE_ORIGINAL_AXIS_DATA = 'storeOriginal';

const CONFIGURATION_CONFIG = 'config';
const CONFIGURATION_GENERATED_LAYOUT = 'generatedLayout';
const CONFIGURATION_REPORTS = 'reports';
const CONFIGURATION_HELP_MESSAGE = 'helpMessage';
const CONFIGURATION_DISABLE_EXPORT = 'disableExport';
const CONFIGURATION_RELOADABLE = 'reloadable';
const CONFIGURATION_CAPTION = 'caption';
const CONFIGURATION_VIEWS = 'views';

const TICK_LABEL_MAX_LENGTH = 30;

const STATIC_EMPTY_OBJECT = {};
const STATIC_EMPTY_ARRAY = [];

export class ReportConfig {
  constructor(rawData = {}, id = undefined) {
    this.rawData = {...rawData};
    this.id = id;
  }

  getId() {
    return this.id;
  }

  getDataSource() {
    return this.rawData[REPORT_DATA_SOURCE];
  }

  getLabel() {
    return this.rawData[REPORT_LABEL];
  }

  getOptions() {
    return this.rawData[REPORT_OPTIONS] || STATIC_EMPTY_OBJECT;
  }

  getType() {
    return this.rawData[REPORT_TYPE];
  }
}

export class SummaryReportData {
  static mapBindings(data, dataBindings) {
    const mappedData = {};

    forOwn(dataBindings, (binding, key) => {
      mappedData[key] = data[binding];
    });

    return mappedData;
  }

  constructor(rawData = {}, id = undefined) {
    this.rawData = {...rawData};
    this.id = id;
  }

  getId() {
    return this.id;
  }

  getDataBindings() {
    return this.rawData[REPORT_SUMMARY_DATA_DATA_BINDINGS];
  }

  getType() {
    return this.rawData[REPORT_SUMMARY_DATA_TYPE];
  }

  getValue() {
    return this.rawData[REPORT_SUMMARY_DATA_VALUE];
  }
}

export class SummaryReportOptions {
  static initData(rawData) {
    const data = [];
    if (rawData) {
      forEach(rawData, (dataItem) => {
        const dataId = dataItem[REPORT_SUMMARY_DATA_ID] || utils.generateGUID();
        data.push(new SummaryReportData(dataItem, dataId));
      });
    }
    return data;
  }

  constructor(rawData = {}) {
    this.rawData = {...rawData};
    this.data = [];
    this.dataMap = {};

    if (this.rawData) {
      this.data = SummaryReportOptions.initData(this.rawData[REPORT_SUMMARY_DATA]);
      // eslint-disable-next-line no-use-before-define
      this.dataMap = ReportsConfig.initReportsMap(this.data);
      if (this.rawData[REPORT_SUMMARY_GENERATED_LAYOUT]) {
        this.generatedLayout = new layoutConfigModel.LayoutConfig(this.rawData[REPORT_SUMMARY_GENERATED_LAYOUT]);
      }
    }
  }

  getData() {
    return this.data;
  }

  getDataMap() {
    return this.dataMap;
  }

  getGeneratedLayout() {
    return this.generatedLayout;
  }

  getLayout() {
    return this.rawData[REPORT_CHART_LAYOUT];
  }
}

export class SummaryReportConfig extends ReportConfig {
  constructor(rawData = {}, id = undefined) {
    super(rawData, id);

    this.options = new SummaryReportOptions(super.getOptions());
  }

  getOptions() {
    return this.options;
  }
}

export class ChartReportDataBinding {
  constructor(rawData = {}) {
    this.rawData = {...rawData};
  }

  getType() {
    return this.rawData[DATA_BINDING_TYPE];
  }

  getData() {
    return this.rawData[DATA_BINDING_DATA];
  }

  getTraceProperty() {
    return this.rawData[DATA_BINDING_TRACE_PROPERTY];
  }

  getTraceValue() {
    return this.rawData[DATA_BINDING_TRACE_VALUE];
  }

  getStoreOriginal(): string[] {
    return this.rawData[DATA_BINDING_STORE_ORIGINAL_AXIS_DATA];
  }
}

export class ChartReportOptions {
  static mapDataSection(boundDataSection, data, dataBindingsData, storeOriginalAxis) {
    forOwn(dataBindingsData, (dataBinding, prop) => {
      boundDataSection[prop] = map(data, (item) => get(item, dataBinding));
      if (storeOriginalAxis) {
        forEach(storeOriginalAxis, (axis, testVariable) => {
          if (axis === prop) {
            boundDataSection[`original_${axis}`] = map(data, (item) => get(item, dataBinding));
          }
        });
      }
    });
  }

  static translateLabels(data: any, translationConfig: any, enumMap: enumContainer.EnumMap | undefined, translateService: IntlShape) {
    if (data && Array.isArray(data)) {
      forEach(data, (trace) => {
        if (trace.name && translateService.messages[trace.name]) {
          trace.name = translateService.formatMessage({id: trace.name});
        }
        forOwn(trace, (value, key) => {
          if (key === 'x' || key === 'y') {
            const axisName = key;
            if (trace[axisName] && Array.isArray(trace[axisName])) {
              const translatedTrace = [...trace[axisName]];
              forEach(trace[axisName], (dataPoint, idx) => {
                if (translationConfig && translationConfig[axisName]) {
                  if (translationConfig[axisName][REPORT_CHART_TRANSLATION_PREFIX]) {
                    dataPoint = `${translationConfig[axisName][REPORT_CHART_TRANSLATION_PREFIX]}.${dataPoint}`;
                  } else if (translationConfig[axisName][REPORT_CHART_TRANSLATION_ENUM_NAME]) {
                    dataPoint = enumContainer.getEnumMessageKeyFromEnumMap(
                      enumMap,
                      translationConfig[axisName][REPORT_CHART_TRANSLATION_ENUM_NAME],
                      dataPoint
                    );
                  }
                }
                if (typeof dataPoint === 'string' && translateService.messages[dataPoint]) {
                  translatedTrace[idx] = translateService.formatMessage({
                    id: dataPoint,
                  });
                }
              });
              trace[axisName] = translatedTrace;
            }
          }
        });
      });
    }
    return data;
  }

  static localizeChartLayout(layout, translateService: IntlShape) {
    if (layout) {
      const newLayout = {...layout};
      forOwn(layout, (layoutItem, index) => {
        if (layoutItem[REPORT_CHART_AXIS_TICKFORMAT] && layoutItem[REPORT_CHART_AXIS_TICKFORMAT] === REPORT_CHART_DATE_LOCALE) {
          newLayout[index] = {
            ...newLayout[index],
            [REPORT_CHART_AXIS_TICKFORMAT]: utils.DateUtils.getLocaleD3FormatPattern(translateService.locale),
          };
        }

        if (layoutItem[REPORT_CHART_AXIS_TICKFORMAT] && layoutItem[REPORT_CHART_AXIS_TICKFORMAT] === REPORT_CHART_DATE_TIME_LOCALE) {
          newLayout[index] = {
            ...newLayout[index],
            [REPORT_CHART_AXIS_TICKFORMAT]: utils.DateUtils.getLocaleD3TimeFormatPattern(translateService.locale),
          };
        }

        if (layoutItem[REPORT_CHART_AXIS_TICKFORMATSTOPS] === REPORT_CHART_DATE_TIME_LOCALE_STOPS) {
          newLayout[index] = {
            ...newLayout[index],
            [REPORT_CHART_AXIS_TICKFORMATSTOPS]: utils.DateUtils.getLocaleD3TimeTickFormatStops(translateService.locale),
          };
        }

        if (layoutItem[REPORT_CHART_AXIS_TITLE]) {
          newLayout[index] = {
            ...newLayout[index],
            [REPORT_CHART_AXIS_TITLE]: utils.TranslateUtils.translate(layoutItem[REPORT_CHART_AXIS_TITLE], translateService),
          };
        }
      });
      return newLayout;
    }
    return layout;
  }

  static mergePropsConfigWithLocalConfig(additionalConfig: Partial<Layout>, localConfig: Partial<Layout>) {
    return merge(additionalConfig, localConfig);
  }

  /**
   * Detect chart orientation.
   * in our vertical charts we usually show string (date) labels on x axis and values on y axis, while
   * in our horizontal charts we usually show values on x axis and string (date) labels on y axis
   * @param traceOrientation
   * @returns {{valueDimensionAxis: string, textDimension: string, valueDimension: string, textDimensionAxis: string}}
   */
  static getTextAndValueDimensions(traceOrientation) {
    let textDimension = 'x';
    let textDimensionAxis = 'xaxis';
    let valueDimension = 'y';
    let valueDimensionAxis = 'yaxis';
    if (traceOrientation === 'h') {
      textDimension = 'y';
      textDimensionAxis = 'yaxis';
      valueDimension = 'x';
      valueDimensionAxis = 'xaxis';
    }
    return {
      textDimension,
      textDimensionAxis,
      valueDimension,
      valueDimensionAxis,
    };
  }

  /**
   * Determine if data is not empty.
   * Data is not empty if at least one value in one trace is not 0.
   * @param boundDataSection
   * @returns {boolean}
   */
  static isNonEmptyDataValues(boundDataSection) {
    let isNonEmpty = false;
    if (boundDataSection) {
      const {valueDimension} = ChartReportOptions.getTextAndValueDimensions(boundDataSection.orientation);
      if (Array.isArray(boundDataSection)) {
        isNonEmpty = some(
          boundDataSection,
          (dataTrace) => dataTrace && some(dataTrace[valueDimension], (traceItemValue) => traceItemValue != null)
        );
      }
    }
    return isNonEmpty;
  }

  static trimAxisTickLabels(data, layout) {
    let resultLayout = layout;
    if (layout && !layout[REPORT_CHART_LAYOUT_SKIP_AXIS_TICK_LABEL_TRIMMING] && data && data.length > 0) {
      let detectedChartOrientation;
      let detectedChartTextDimensionAxis;
      let traceOrientationMismatch = false;
      const tracesTickVals = [];
      // collect tickVals from all traces
      data.forEach((trace) => {
        // TODO: 2021-02-26 JM - we are handling now only single text dimension axis,
        //  there can be multiple ones (xaxis, xaxis2; yaxis, yaxis2)
        const {textDimension, textDimensionAxis} = ChartReportOptions.getTextAndValueDimensions(trace.orientation);

        detectedChartOrientation = detectedChartOrientation || trace.orientation;
        detectedChartTextDimensionAxis = detectedChartTextDimensionAxis || textDimensionAxis;

        if (detectedChartOrientation !== trace.orientation) {
          traceOrientationMismatch = true;
        } else if (trace[textDimension] && trace[textDimension].length > 0 && isString(trace[textDimension][0])) {
          // if textDimension really holds string data
          tracesTickVals.push(trace[textDimension]);
        }
      });
      if (traceOrientationMismatch) {
        console.warn('Chart has traces with different orientation. Cannot apply trimming of tick labels');
      } else {
        const tickvals = union(...tracesTickVals);
        const tickLabelMaxLength = layout[REPORT_CHART_LAYOUT_TICK_LABEL_MAX_LENGTH] || TICK_LABEL_MAX_LENGTH;
        resultLayout = {
          ...layout,
          [detectedChartTextDimensionAxis]: {
            ...layout[detectedChartTextDimensionAxis],
            tickvals,
            // trim tick labels on textDimension axis
            ticktext: tickvals.map(
              (traceDataItem) => traceDataItem.substr(0, tickLabelMaxLength) + (traceDataItem.length > tickLabelMaxLength ? '...' : '')
            ),
          },
        };
      }
    }
    return resultLayout;
  }

  static applyHoverTemplate(data, layout, textDimensionLabelPrefix, translateService) {
    if (data && data.length > 0) {
      data.forEach((trace) => {
        // TODO: 2021-02-26 JM - we are handling now only single text dimension axis,
        //  there can be multiple ones (xaxis and xaxis2, or yaxis and yaxis2)
        const {textDimension, valueDimension} = ChartReportOptions.getTextAndValueDimensions(trace.orientation);
        // check if textDimension really holds string data
        if (
          (!layout || !layout[REPORT_CHART_LAYOUT_SKIP_HOVER_TEMPLATE]) &&
          trace[textDimension] &&
          trace[textDimension].length > 0 &&
          isString(trace[textDimension][0])
        ) {
          // store full textDimension labels in meta array (text property)
          trace.meta = trace[textDimension].map((text) => ({text}));

          const textDimensionLabelPrefixTemplate = textDimensionLabelPrefix
            ? `<i>${translateService.formatMessage({
                id: textDimensionLabelPrefix,
              })}</i>: `
            : '';
          let valueDimensionLabelPrefixTemplate = '';
          if (trace.name) {
            // store trace name in meta array (name property)
            trace.meta = trace.meta.map((item) => ({
              ...item,
              name: trace.name,
            }));
            valueDimensionLabelPrefixTemplate = '<b>%{meta.name}</b>: ';
          }
          // define hovertemplate
          // content of <extra> tag is rendered next to tooltip (by default it displays trace name),
          // we purposely want to show nothing (i.e. hide trace name) in it here
          trace.hovertemplate = `${textDimensionLabelPrefixTemplate}%{meta.text}<br>${valueDimensionLabelPrefixTemplate}%{${valueDimension}}<extra></extra>`;
        }
      });
    }
    return data;
  }

  static getBoundDataSection(data, dataBindings, dataSection) {
    let boundDataSection = [];

    if (data && dataBindings && dataSection) {
      forEach(dataSection, (dataChart, idx) => {
        boundDataSection[idx] = {...dataChart};
        // TRACE_FROM_DATA - select first and only data binding
        const dataBindingsItem = dataBindings[idx] || dataBindings[0];
        if (dataBindingsItem) {
          if (dataBindingsItem.getType() === DATA_BINDING_TYPE_SIMPLE) {
            ChartReportOptions.mapDataSection(boundDataSection[idx], data, dataBindingsItem.getData(), dataBindingsItem.getStoreOriginal());
          }
          if (dataBindingsItem.getType() === DATA_BINDING_TYPE_TRACE) {
            const traceProperty = dataBindingsItem.getTraceProperty();
            const traceValue = dataBindingsItem.getTraceValue();

            const traceData = filter(data, (dataItem) => {
              return dataItem[traceProperty] === traceValue;
            });
            ChartReportOptions.mapDataSection(
              boundDataSection[idx],
              traceData,
              dataBindingsItem.getData(),
              dataBindingsItem.getStoreOriginal()
            );
          }

          if (dataBindingsItem.getType() === DATA_BINDING_TYPE_TRACE_FROM_DATA) {
            const traceProperty = dataBindingsItem.getTraceProperty();
            const traceValue = dataChart.name;
            const traceData = filter(data, (dataItem) => dataItem[traceProperty] === traceValue);
            ChartReportOptions.mapDataSection(
              boundDataSection[idx],
              traceData,
              dataBindingsItem.getData(),
              dataBindingsItem.getStoreOriginal()
            );
          }
        }
      });
    } else {
      boundDataSection = dataSection;
    }
    return boundDataSection;
  }

  static initDataBindings(rawDataBindings) {
    const dataBindings = [];
    if (rawDataBindings) {
      forEach(rawDataBindings, (dataBindingsItem) => {
        dataBindings.push(new ChartReportDataBinding(dataBindingsItem || []));
      });
    }
    return dataBindings;
  }

  constructor(rawData = {}) {
    this.rawData = {...rawData};

    this.dataBindings = ChartReportOptions.initDataBindings(this.rawData[REPORT_CHART_DATA_BINDINGS]);
  }

  getConfig() {
    return this.rawData[REPORT_CHART_CONFIG];
  }

  getData() {
    return this.rawData[REPORT_CHART_DATA];
  }

  getLayout() {
    return this.rawData[REPORT_CHART_LAYOUT];
  }

  getLayoutTextDimensionHoverLabel() {
    return this.rawData[REPORT_CHART_LAYOUT] ? this.rawData[REPORT_CHART_LAYOUT][REPORT_CHART_LAYOUT_TEXT_DIMENSION_HOVER_LABEL] : null;
  }

  getStyle() {
    return this.rawData[REPORT_CHART_STYLE];
  }

  getClass() {
    return this.rawData[REPORT_CHART_CLASS];
  }

  getUseResizeHandler() {
    return this.rawData[REPORT_CHART_RESIZE];
  }

  getEmptyMessage() {
    return this.rawData[REPORT_CHART_EMPTY_MESSAGE];
  }

  getDataBindings() {
    return this.dataBindings;
  }

  getDownloadedImageTitle() {
    return this.rawData[REPORT_DOWNLOADED_IMAGE_TITLE];
  }

  getTranslationConfig() {
    return this.rawData[REPORT_CHART_TRANSLATION_CONFIG];
  }
}

export class ChartReportConfig extends ReportConfig {
  constructor(rawData = {}, id: string | undefined = undefined) {
    super(rawData, id);

    this.options = new ChartReportOptions(super.getOptions());
  }

  getDataType() {
    return this.rawData[REPORT_DATA_TYPE];
  }

  getOptions() {
    return this.options;
  }

  getSourceDataKey() {
    return this.rawData[REPORT_DATA_SOURCE];
  }
}

export class TableReportLayout {
  constructor(rawData = {}) {
    this.rawData = {...rawData};
  }

  getRawData() {
    return this.rawData;
  }
}

export class TableReportOptions {
  static initTableConfig(rawTableConfig, id) {
    let tableConfig = {};
    if (rawTableConfig) {
      tableConfig = {...rawTableConfig};
    }
    return new TableConfig(tableConfig, id, TABLE_TYPE_AG);
  }

  constructor(rawData = {}, id = undefined, paging = false) {
    this.rawData = {...rawData};

    this.tableConfig = TableReportOptions.initTableConfig(this.rawData[TABLE_CONFIG], id);
    this.layout = new TableReportLayout(this.rawData[REPORT_TABLE_LAYOUT]);
    this.paging = paging;
  }

  getTableConfig() {
    return this.tableConfig;
  }

  getLayout() {
    return this.layout;
  }

  isPagingReport() {
    return this.paging;
  }

  getClass() {
    return this.rawData[REPORT_CHART_CLASS];
  }
}

export class TableReportConfig extends ReportConfig {
  constructor(rawData = {}, id = undefined, paging = false) {
    super(rawData, id);

    // override default (super) options
    const tableId = `${this.getDataSource()}_${this.getId()}`;
    this.options = new TableReportOptions(super.getOptions(), tableId, paging);
  }

  getOptions() {
    return this.options;
  }

  getSourceDataKey() {
    return this.rawData[REPORT_DATA_SOURCE];
  }
}

export class ReportsConfig {
  static initReports(rawReports) {
    const reports = [];
    if (rawReports) {
      forEach(rawReports, (report, idx) => {
        let newReport;
        const reportId = report[REPORT_ID] || idx;
        switch (report[REPORT_TYPE]) {
          case REPORT_TYPE_CHART: {
            newReport = new ChartReportConfig(report, reportId);
            break;
          }
          case REPORT_TYPE_SUMMARY: {
            newReport = new SummaryReportConfig(report, reportId);
            break;
          }
          case REPORT_TYPE_TABLE: {
            newReport = new TableReportConfig(report, reportId);
            break;
          }
          case REPORT_TYPE_PAGING_TABLE: {
            newReport = new TableReportConfig(report, reportId, true);
            break;
          }
        }
        reports.push(newReport);
      });
    }
    return reports;
  }

  static initReportsMap(reports) {
    const reportsMap = {};
    if (reports) {
      forEach(reports, (report) => {
        reportsMap[report.getId()] = report;
      });
    }
    return reportsMap;
  }

  static collectDataSources(reportsMap, generatedLayout) {
    const dataSources = [];
    forEach(generatedLayout.getDataIds(), (reportId) => {
      if (!isEmpty(reportsMap[reportId]) && !isEmpty(reportsMap[reportId].getDataSource())) {
        // get data source from report definition
        dataSources.push(reportsMap[reportId].getDataSource());
      }
    });
    return dataSources;
  }

  static initDataSourcesByView(reportsMap, views, generatedLayout) {
    const dataSourcesByViews = {};
    if (!isEmpty(views)) {
      // get report names from generated layout of views
      forOwn(views, (view, viewId) => {
        if (!isEmpty(view) && view.getGeneratedLayout && view.getGeneratedLayout()) {
          dataSourcesByViews[viewId] = ReportsConfig.collectDataSources(reportsMap, view.getGeneratedLayout());
        }
      });
    } else if (generatedLayout) {
      dataSourcesByViews.document = ReportsConfig.collectDataSources(reportsMap, generatedLayout);
    }

    return dataSourcesByViews;
  }

  static initSearchTypeViewReports(views) {
    forOwn(views, (view) => {
      let generatedLayout = {};
      if (view.getRawData) {
        if (view.getRawData()[CONFIGURATION_GENERATED_LAYOUT]) {
          generatedLayout = new layoutConfigModel.LayoutConfig(view.getRawData()[CONFIGURATION_GENERATED_LAYOUT]);
        }
        if (view.initGeneratedLayout) {
          view.initGeneratedLayout(generatedLayout);
        }
      }
    });
  }

  static filterReportsByView(reports, views, viewId = 'document') {
    let reportsByView = reports;
    if (!isEmpty(views) && !isEmpty(views.getView(viewId)) && views.getView(viewId).getItems()) {
      // get reports only from the wanted view
      const view = views.getView(viewId).getItems();
      reportsByView = filter(reports, (report) => includes(view, report.getId()));
    }
    return reportsByView;
  }

  constructor(rawData = {}) {
    this.rawData = {...rawData};
    this.reports = [];
    this.reportsMap = {};
    this.dataSourcesByView = {};
    if (this.rawData && this.rawData[CONFIGURATION_CONFIG] && this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_REPORTS]) {
      this.reports = ReportsConfig.initReports(this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_REPORTS]);
      this.reportsMap = ReportsConfig.initReportsMap(this.reports);
      if (this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_GENERATED_LAYOUT]) {
        this.generatedLayout = new layoutConfigModel.LayoutConfig(this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_GENERATED_LAYOUT]);
      }
      this.views = new SearchTypeViews(this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_VIEWS]);
      ReportsConfig.initSearchTypeViewReports(this.views.getViews());
      this.dataSourcesByView = ReportsConfig.initDataSourcesByView(this.getReportsMap(), this.views.getViews(), this.generatedLayout);
      this.loaded = true;
    }
  }

  getReports() {
    return this.reports;
  }

  getReportsMap() {
    return this.reportsMap;
  }

  getReportById(id) {
    return this.reportsMap[id];
  }

  getGeneratedLayout(viewId = 'document') {
    const views = this.getViews();
    return !isEmpty(views) &&
      !isEmpty(views.getView(viewId)) &&
      views.getView(viewId).getGeneratedLayout &&
      views.getView(viewId).getGeneratedLayout()
      ? views.getView(viewId).getGeneratedLayout()
      : this.generatedLayout;
  }

  getDataSources(viewId = 'document') {
    return this.dataSourcesByView[viewId] || STATIC_EMPTY_ARRAY;
  }

  getDisableExport() {
    return this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_DISABLE_EXPORT];
  }

  getHelpMessage() {
    return this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_HELP_MESSAGE];
  }

  getReloadable() {
    return this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_RELOADABLE];
  }

  getCaption(viewId = 'document') {
    const views = this.getViews();
    const configCaption =
      this.rawData[CONFIGURATION_CONFIG] && this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_CAPTION]
        ? this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_CAPTION]
        : '';

    return !isEmpty(views) && !isEmpty(views.getView(viewId)) && views.getView(viewId).getCaption && views.getView(viewId).getCaption()
      ? views.getView(viewId).getCaption()
      : configCaption;
  }

  getViews() {
    return this.views;
  }

  getReportsByView(viewId = 'document') {
    return ReportsConfig.filterReportsByView(this.getReports(), this.getViews(), viewId);
  }

  isLoaded() {
    return this.loaded;
  }

  isPaging() {
    return (
      this.getReports() &&
      this.getReports().length === 1 &&
      this.getReports()[0] instanceof TableReportConfig &&
      this.getReports()[0].getOptions().isPagingReport()
    );
  }

  getPagingReportId() {
    return this.isPaging() ? this.getReports()[0].getId() : undefined;
  }
}

export default {
  ChartReportConfig,
  ChartReportOptions,
  ReportConfig,
  ReportsConfig,
  SummaryReportConfig,
  SummaryReportData,
  SummaryReportOptions,
  TableReportConfig,
  TableReportLayout,
  TableReportOptions,
};
